<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-finance me-2"></i>Revenue by Department &amp; Activity</h4>
        </div>
        <div class="d-flex gap-2">
          <input type="date" class="hms-input hms-filter" v-model="dateFrom" />
          <input type="date" class="hms-input hms-filter" v-model="dateTo" />
          <button class="hms-btn hms-btn-blue" @click="load" :disabled="loading"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
        </div>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else>
        <div class="hms-stat-row mb-3">
          <div class="hms-stat"><div class="hms-stat-num">KES {{ fmt(total) }}</div><div class="hms-stat-lbl">Billed · {{ dateFrom }} → {{ dateTo }}</div></div>
          <div class="hms-stat">
            <div class="hms-stat-num">KES {{ fmt(collectedTotal) }}</div>
            <div class="hms-stat-lbl">Collected (payments)</div>
            <div v-if="collectedPrior > 0" class="hms-muted-sm mt-1" style="font-size:11px;">
              incl. KES {{ fmt(collectedPrior) }} for invoices billed before this period
            </div>
          </div>
          <div class="hms-stat"><div class="hms-stat-num">{{ rows.length }}</div><div class="hms-stat-lbl">Departments billing</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ topDeptLabel }}</div><div class="hms-stat-lbl">Top earner</div></div>
        </div>

        <div class="hms-card">
          <div style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr><th>Department</th><th>Activity</th><th class="text-end">Lines</th><th class="text-end">Units</th><th class="text-end">Revenue (KES)</th><th class="text-end">% of total</th></tr></thead>
              <tbody>
                <tr v-for="r in rows" :key="r.category">
                  <td><strong>{{ r.department }}</strong></td>
                  <td class="hms-muted-sm">{{ r.category }}</td>
                  <td class="text-end">{{ r.line_count }}</td>
                  <td class="text-end">{{ fmt(r.units) }}</td>
                  <td class="text-end fw-bold">{{ fmt(r.revenue) }}</td>
                  <td class="text-end">
                    <div class="d-flex align-items-center justify-content-end gap-2">
                      <div class="hms-bar"><div class="hms-bar-fill" :style="{width: pct(r.revenue)+'%'}"></div></div>
                      <span class="hms-muted-sm" style="min-width:48px; text-align:right;">{{ pct(r.revenue) }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="rows.length"><tr><td colspan="4" class="text-end fw-bold">Total</td><td class="text-end fw-bold">KES {{ fmt(total) }}</td><td></td></tr></tfoot>
            </table>
            <div v-if="!rows.length" class="hms-empty"><i class="mdi mdi-finance"></i> No revenue in this period.</div>
          </div>
        </div>

        <div class="hms-card mt-3">
          <div class="hms-section-label mb-2">COLLECTIONS BY PAYMENT MODE</div>
          <div style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr><th>Payment mode</th><th class="text-end">Transactions</th><th class="text-end">Collected (KES)</th><th class="text-end">% of collections</th></tr></thead>
              <tbody>
                <tr v-for="m in modes" :key="m.payment_method">
                  <td><strong>{{ modeLabel(m.payment_method) }}</strong></td>
                  <td class="text-end">{{ m.txn_count }}</td>
                  <td class="text-end fw-bold">{{ fmt(m.collected) }}</td>
                  <td class="text-end">{{ collectedTotal > 0 ? Math.round((m.collected / collectedTotal) * 1000) / 10 : 0 }}%</td>
                </tr>
              </tbody>
              <tfoot v-if="modes.length"><tr><td class="text-end fw-bold">Total</td><td></td><td class="text-end fw-bold">KES {{ fmt(collectedTotal) }}</td><td></td></tr></tfoot>
            </table>
            <div v-if="!modes.length" class="hms-empty"><i class="mdi mdi-cash-remove"></i> No payments in this period.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "RevenueByDepartment",
  components: { Navigation },
  data() {
    return {
      loading: true, rows: [], total: 0, modes: [], collectedTotal: 0, collectedPrior: 0,
      dateFrom: this.firstOfMonth(), dateTo: this.today()
    };
  },
  computed: {
    topDeptLabel() { return this.rows.length ? this.rows[0].department : '—'; }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.load();
  },
  methods: {
    firstOfMonth() { var d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-01'; },
    today() { var d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); },
    async load() {
      this.loading = true;
      try {
        var r = await executeJson({ function: "getRevenueByDepartment", date_from: this.dateFrom, date_to: this.dateTo }, hms);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          this.rows = d.by_department || [];
          this.total = +d.total || 0;
          this.modes = d.by_payment_mode || [];
          this.collectedTotal = +d.collected_total || 0;
          this.collectedPrior = +d.collected_prior_invoices || 0;
        }
      } catch (e) { /* ignore */ }
      this.loading = false;
    },
    fmt(v) { return Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }); },
    modeLabel(m) {
      var map = { cash:'Cash', mpesa:'M-Pesa', card:'Card', bank_transfer:'Bank Transfer', insurance:'Insurance', corporate:'Corporate', waiver:'Waiver' };
      return map[m] || m;
    },
    pct(v) { return this.total > 0 ? Math.round((v / this.total) * 1000) / 10 : 0; }
  }
};
</script>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 16px 40px; }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; }
.hms-back:hover { color: var(--hms-primary); }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 60px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-stat-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.hms-section-label { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 16px; }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-input { background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 7px 12px; color: var(--hms-h); font-size: 13px; }
.hms-filter { width: auto; }
.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.text-end { text-align: right; }
.hms-bar { width: 120px; height: 6px; background: var(--hms-bg3); border-radius: 4px; overflow: hidden; }
.hms-bar-fill { height: 100%; background: var(--hms-primary); }
.hms-empty { text-align: center; padding: 30px; color: var(--hms-s); }
</style>
